import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import {
  type CreateEventTemplateRequest,
  CreateEventTemplateRequestSchema,
  type EventChargeMode,
  EventChargeModeSchema,
  type EventPhotoPolicy,
  EventPhotoPolicySchema,
} from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import { TriangleAlert } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import {
  CHARGE_MODE_OPTIONS,
  EVENT_PHOTO_POLICY_OPTIONS,
  EVENT_TEMPLATE_CATEGORY_OPTIONS,
} from '../constants';

// `defaultConfig` (CreateEventTemplateRequestSchema) is a free-form JSON
// record, not a typed shape — so the form validates its own two optional
// UI-level fields (defaultChargeMode/defaultPhotoPolicy) against the SAME
// shared enums the rest of the module uses, then folds them into the
// `defaultConfig` record on submit. name/category reuse the shared schema's
// own field validators (no drift from the backend's CreateEventTemplateDto).
const CreateTemplateFormSchema = z.object({
  name: CreateEventTemplateRequestSchema.shape.name,
  category: CreateEventTemplateRequestSchema.shape.category,
  defaultChargeMode: EventChargeModeSchema.optional().or(z.literal('')),
  defaultPhotoPolicy: EventPhotoPolicySchema.optional().or(z.literal('')),
});
type CreateTemplateFormValues = z.infer<typeof CreateTemplateFormSchema>;

interface CreateEventTemplateFormProps {
  onSubmit: (values: CreateEventTemplateRequest) => Promise<void>;
  disabled?: boolean;
}

export function CreateEventTemplateForm({ onSubmit, disabled }: CreateEventTemplateFormProps) {
  const { t } = useTranslation('events');
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateTemplateFormValues>({
    resolver: zodResolver(CreateTemplateFormSchema),
    defaultValues: {
      name: '',
      category: 'catering',
      defaultChargeMode: '',
      defaultPhotoPolicy: '',
    },
  });

  async function handleFormSubmit(values: CreateTemplateFormValues) {
    setServerError(null);
    const defaultConfig: Record<string, EventChargeMode | EventPhotoPolicy> = {};
    if (values.defaultChargeMode) defaultConfig.chargeMode = values.defaultChargeMode;
    if (values.defaultPhotoPolicy) defaultConfig.photoPolicy = values.defaultPhotoPolicy;

    try {
      await onSubmit({ name: values.name, category: values.category, defaultConfig });
      reset({ name: '', category: 'catering', defaultChargeMode: '', defaultPhotoPolicy: '' });
    } catch (e) {
      setServerError(
        translateApiError(e, t, { fallback: t('createEventTemplateForm.genericError') }),
      );
    }
  }

  const isDisabled = disabled || isSubmitting;

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-3">
      {serverError && <FormBanner message={serverError} />}

      <div className="grid gap-4 sm:grid-cols-[2fr_1fr_1fr_1fr_auto] sm:items-end">
        <Field
          label={t('createEventTemplateForm.name.label')}
          htmlFor="template-name"
          error={errors.name?.message}
        >
          <Input
            id="template-name"
            placeholder={t('createEventTemplateForm.name.placeholder')}
            disabled={isDisabled}
            {...register('name')}
          />
        </Field>

        <Field
          label={t('createEventTemplateForm.category')}
          htmlFor="template-category"
          error={errors.category?.message}
        >
          <SelectInput id="template-category" disabled={isDisabled} {...register('category')}>
            {EVENT_TEMPLATE_CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(`constants.templateCategory.${o.value}`)}
              </option>
            ))}
          </SelectInput>
        </Field>

        <Field
          label={t('createEventTemplateForm.defaultChargeMode')}
          htmlFor="template-charge-mode"
          error={errors.defaultChargeMode?.message}
        >
          <SelectInput
            id="template-charge-mode"
            disabled={isDisabled}
            {...register('defaultChargeMode')}
          >
            <option value="">{t('constants.unset')}</option>
            {CHARGE_MODE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(`constants.chargeMode.${o.value}`)}
              </option>
            ))}
          </SelectInput>
        </Field>

        <Field
          label={t('createEventTemplateForm.defaultPhotoPolicy')}
          htmlFor="template-photo-policy"
          error={errors.defaultPhotoPolicy?.message}
        >
          <SelectInput
            id="template-photo-policy"
            disabled={isDisabled}
            {...register('defaultPhotoPolicy')}
          >
            <option value="">{t('constants.unset')}</option>
            {EVENT_PHOTO_POLICY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(`constants.photoPolicy.${o.value}`)}
              </option>
            ))}
          </SelectInput>
        </Field>

        <Button type="submit" size="sm" disabled={isDisabled}>
          {isSubmitting
            ? t('createEventTemplateForm.creating')
            : t('createEventTemplateForm.createTemplate')}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
        {label}
      </Label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
          {error}
        </p>
      )}
    </div>
  );
}

const SelectInput = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function SelectInput({ className, ...rest }, ref) {
    return (
      <select
        ref={ref}
        {...rest}
        className={`flex h-10 w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 text-[14px] text-jq-charcoal shadow-sm transition focus:border-jq-charcoal focus:outline-none focus:ring-1 focus:ring-jq-charcoal disabled:cursor-not-allowed disabled:opacity-50 ${
          className ?? ''
        }`}
      />
    );
  },
);

function FormBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
    </div>
  );
}
